/**
 * «Запретить писать сообщение пока выполняется генерация» (owner, 2026-10-18,
 * INV-PH-137): while the visitor's own turn is streaming nothing can be sent —
 * the composer is read-only, chips and card buttons are inactive.
 *
 * `App.tsx` keeps WHICH turn is streaming in a ref (`turnKind`), which no
 * component can subscribe to. This is that same value mirrored into a store,
 * written only through `setTurnKind`, so a control can ask «is a send ignored
 * right now?» (`useSendLocked`) with the very rule `enqueueOrSend` applies
 * (`sendQueue.ts`'s `isSendLocked`). A background `peterhof_live` report run
 * does not lock anything: a message sent over it is queued, as before.
 */

import { create } from 'zustand';
import { useChat } from '../store/chatStore';
import { isSendLocked, type TurnKind } from './sendQueue';

export const useTurnKind = create<{ kind: TurnKind }>(() => ({ kind: null }));

export function setTurnKind(kind: TurnKind): void {
  if (useTurnKind.getState().kind !== kind) useTurnKind.setState({ kind });
}

/** `true` while a send would be ignored — the visitor's own turn is in flight. */
export function useSendLocked(): boolean {
  const status = useChat((state) => state.status);
  const kind = useTurnKind((state) => state.kind);
  return isSendLocked(status, kind);
}

/**
 * The composer's submit, without React: what Enter and the send button do.
 * Nothing happens (and the draft is NOT cleared) while the field is locked, on
 * the 429 cooldown, or with nothing to send; otherwise the draft is cleared
 * BEFORE the send, as ever (`dispatch` and the report scheduler read it), and
 * put back if `onSend` did not take the message. → whether it went out.
 */
/**
 * «Стоп» takes the send button's place the moment a turn starts, so the second click of a double click on
 * «Отправить» landed on it and cancelled the answer just asked for (first-user QA, dev, 21.10.2026). A click
 * that is a double click's second, or one this soon after the turn started, is not a «Стоп».
 */
export const STOP_GRACE_MS = 700;

export function isStrayStop(clickDetail: number, busySinceMs: number | null, nowMs: number): boolean {
  return clickDetail > 1 || (busySinceMs !== null && nowMs - busySinceMs < STOP_GRACE_MS);
}

export function submitDraft(args: {
  draft: string;
  cooldown: number;
  sendDisabled: boolean;
  locked: boolean;
  onSend: (text: string) => boolean | void;
  setDraft: (draft: string) => void;
}): boolean {
  const text = args.draft.trim();
  // a field of spaces is cleared, not kept: it sent nothing and hid the placeholder (first-user QA, 21.10.2026)
  if (!text && args.draft && !args.locked) args.setDraft('');
  if (!text || args.cooldown > 0 || args.sendDisabled || args.locked) return false;
  args.setDraft('');
  if (args.onSend(text) === false) {
    args.setDraft(args.draft);
    return false;
  }
  return true;
}

/** The disclaimer under the input. */
export const NOTE = 'ГигаЧат может ошибаться — проверяйте его ответы';
/** …and while the guide answers over a typed draft: the draft is kept, and when it goes (QA dev 30.10.2026, С7-4 —
 * the text stayed in a read-only field with no word why, INV-PH-137 keeps the lock). */
export const DRAFT_WAITS = 'Гид ещё отвечает — ваш текст сохранён, отправьте его после ответа.';

export function composerNote(locked: boolean, draft: string): string {
  return locked && draft.trim() ? DRAFT_WAITS : NOTE;
}
